import { getDevUser, getEnvironment } from './config';

/**
 * Returns the inbox.* Cara address for the current environment. This is the
 * Reply-To set on Cara's AI-response emails and the address users should send
 * mail to Cara at (Email AI). Trigger aliases use a different local-part shape —
 * see getCaraTriggerEmailAddress.
 */
export const getCaraInboundAIEmailAddress = (): string => {
  switch (getEnvironment()) {
    case 'production':
      return 'cara@inbox.getcara.ai';
    case 'staging':
      return 'cara-staging@inbox.getcara.ai';
    case 'dev':
      return `cara-dev-${getDevUser() ?? 'dev'}@inbox.getcara.ai`;
    default:
      return 'cara-unknown@inbox.getcara.ai';
  }
};

/**
 * Slug used when deriving a handle from free text. May contain hyphens as word
 * separators; claimable handles must be a single token — see
 * suggestAgencyEmailNamespace / sanitizeAgencyEmailNamespaceInput.
 */
const slugifyCaraEmailNamespace = (value: string): string => {
  const slug = value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
  return slug || 'agency';
};

/**
 * Strips anything that is not a single-token handle (lowercase alphanumeric).
 */
export const sanitizeAgencyEmailNamespaceInput = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 32);

/**
 * Proposes a short agency email handle from a display/legal name
 * (e.g. "Oyster Insurance" → "oyster"). Always a single token (no hyphens).
 * Callers must still claim uniqueness.
 */
export const suggestAgencyEmailNamespace = (name: string): string => {
  let slug = slugifyCaraEmailNamespace(name);
  for (const suffix of [
    '-insurance',
    '-insurances',
    '-agency',
    '-agencies',
    '-group',
    '-llc',
    '-inc',
    '-corp',
    '-co',
    '-company',
    '-brokers',
    '-brokerage'
  ]) {
    if (slug.endsWith(suffix)) {
      slug = slug.slice(0, -suffix.length);
    }
  }
  const first = slug.split('-').find(Boolean) ?? '';
  return sanitizeAgencyEmailNamespaceInput(first);
};

/**
 * Client-facing Cara trigger alias from agency handle + per-trigger suffix only.
 * Production/dev: `cara-{namespace}-{suffix}@inbox.getcara.ai`. Staging adds a
 * `staging` local-part segment automatically (not user-configured).
 *
 * Identity vs authorization (v1):
 * - Users configure handle (agency) and suffix (trigger); this function builds the To address.
 * - Agency / agency-user are still resolved from the sender (From must be an
 *   agency user). A later allowlist can open external senders without changing
 *   this address format — namespace on To becomes the agency key then.
 *
 * Delivery still needs Mailgun catch-all (or per-alias routes) on
 * inbox.getcara.ai; until then the address will bounce.
 */
/** Extra local-part segment so Mailgun can route staging triggers separately from production. */
const caraTriggerEnvLocalSegment = (): string | null => {
  if (getEnvironment() === 'staging') {
    return 'staging';
  }
  return null;
};

export const getCaraTriggerEmailAddress = (namespace: string, suffix: string): string => {
  // Claimed handles are a single token; display names fall back to suggest (first word).
  const ns = suggestAgencyEmailNamespace(namespace) || sanitizeAgencyEmailNamespaceInput(namespace) || 'agency';
  const localSuffix = suffix.trim().toLowerCase().replace(/[^a-z0-9-]+/g, '') || '…';
  const envSeg = caraTriggerEnvLocalSegment();
  const localPart = envSeg ? `cara-${envSeg}-${ns}-${localSuffix}` : `cara-${ns}-${localSuffix}`;
  return `${localPart}@inbox.getcara.ai`;
};
